// Manager base class adapted from AgentGroupChat for React environment
// Transforms the class-based manager pattern into React-compatible hooks/contexts

import { useState, useEffect } from 'react';

export class Manager {

  // Manager lifecycle adapted for React hooks
  async init() {
    await this.initGlobalVariables();
    await this.initSystems();
    await this.initEventListeners();
    await this.initStates();
  }

  /**
   * Override this method in the subclass to initialize global variables.
   * In React: Use useState or useContext for global state
   */
  async initGlobalVariables() {}

  /**
   * Override this method in the subclass to initialize systems.
   * In React: Use useEffect to listen for events
   */
  async initSystems() {}

  /**
   * Override this method in the subclass to initialize event listeners.
   * In React: Use useEffect to listen for events
   */
  async initEventListeners() {}

  /**
   * Override this method in the subclass to initialize states.
   * In React: Use useState, useReducer, or context state
   */
  async initStates() {}

  /**
   * Utility method to create React-compatible state management
   * Can be used by subclasses to maintain compatibility patterns
   */
  createState(initialValue) {
    // This would be used within React hooks
    // Subclasses should use React hooks directly
    console.warn('createState() is for compatibility - use React hooks directly');
    return initialValue;
  }

  /**
   * Utility method for async operations with error handling
   * Compatible with React patterns
   */
  async handleAsync(operation, options = {}) {
    const {
      onSuccess,
      onError,
      onFinally,
      showLogs = true
    } = options;

    try {
      if (showLogs && window.logger) {
        window.logger.log({
          tags: "manager|async",
          color1: "cyan"
        }, `Starting async operation: ${operation.name || 'anonymous'}`);
      }

      const result = await operation();

      if (onSuccess) {
        await onSuccess(result);
      }

      if (showLogs && window.logger) {
        window.logger.log({
          tags: "manager|async|success",
          color1: "lime"
        }, `Async operation completed successfully`);
      }

      return result;
    } catch (error) {
      if (showLogs && window.logger) {
        window.logger.error({
          tags: "manager|async|error",
          color1: "red",
          color2: "orange",
          includeSource: true
        }, `Async operation failed:`, error);
      }

      if (onError) {
        await onError(error);
      }

      throw error;
    } finally {
      if (onFinally) {
        await onFinally();
      }
    }
  }

  /**
   * Cleanup method for React useEffect cleanup functions
   */
  cleanup() {
    // Override in subclasses for cleanup logic
    // In React: return from useEffect cleanup function
  }
}

// React Hook Adapter for Manager Pattern
// Allows using Manager classes within React hooks
export function useManager(ManagerClass, ...args) {
  const [manager] = useState(() => new ManagerClass(...args));
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeManager = async () => {
      try {
        await manager.init();
        setIsInitialized(true);

        if (window.logger) {
          window.logger.log({
            tags: "manager|init|react",
            color1: "lime"
          }, `${ManagerClass.name} initialized in React hook`);
        }
      } catch (error) {
        if (window.logger) {
          window.logger.error({
            tags: "manager|init|error|react",
            color1: "red",
            color2: "orange",
            includeSource: true
          }, `Failed to initialize ${ManagerClass.name} in React hook:`, error);
        }
      }
    };

    initializeManager();

    // Cleanup on unmount
    return () => {
      if (manager.cleanup) {
        manager.cleanup();
      }
    };
  }, [manager, ManagerClass]);

  return { manager, isInitialized };
}

// Higher-order component for manager integration
export function withManager(ManagerClass, managerPropName = 'manager') {
  return function(WrappedComponent) {
    return function WithManagerComponent(props) {
      const { manager, isInitialized } = useManager(ManagerClass);

      return (
        <WrappedComponent
          {...props}
          {...{[managerPropName]: manager}}
          managerInitialized={isInitialized}
        />
      );
    };
  };
}
